/**
 * Preserve a scroll container's read position across a content *prepend* that
 * has no native scroll anchor (virtual scrollers such as vue-virtual-scroller
 * have no `prependItems`).
 *
 * Anchoring is by distance-from-bottom: inserting older content above the
 * viewport does not change how far the user is from the bottom, so holding
 * that distance keeps the same content visible. Virtual scrollers resolve real
 * item heights progressively (estimated size first, then ResizeObserver), so
 * the position must be re-applied across a short settle window rather than once
 * — a single correction would jump as soon as heights are measured.
 *
 * Usage:
 *   const restore = captureScrollAnchor(scrollEl);
 *   await loadOlderItems();
 *   await nextTick();          // let the new items commit to the DOM
 *   await restore();           // holds position until heights settle
 */
export function captureScrollAnchor(
  el: HTMLElement | null | undefined,
): (settleMs?: number) => Promise<void> {
  const distanceFromBottom = el ? el.scrollHeight - el.scrollTop : 0;

  return function restoreScrollAnchor(settleMs = 400): Promise<void> {
    if (!el) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const start = performance.now();
      const tick = () => {
        // The element may have detached (route change / unmount) mid-settle.
        if (!el.isConnected) return resolve();
        el.scrollTop = el.scrollHeight - distanceFromBottom;
        if (performance.now() - start < settleMs) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });
  };
}
